import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loanProfilingApi } from '@/services/api';
import { Field, OfferUpload, DocLink } from './OfferPieces';
import { inr } from './CockpitUI';

const FIELDS = [
  ['loan_amount', 'Loan amount', 'number'],
  ['loan_suraksha', 'Loan Suraksha', 'number'],
  ['total_loan_amount', 'Total loan amount', 'number'],
  ['tenure_months', 'Tenure (months)', 'number'],
  ['emi_amount', 'EMI amount', 'number'],
  ['roi', 'Rate of interest (%)', 'number'],
  ['stamp_duty', 'Stamp duty', 'number'],
  ['processing_fee', 'Processing fee', 'number'],
  ['other_deductions', 'Other deductions', 'number'],
  ['net_disbursement_amount', 'Net disbursement', 'number'],
  ['emi_start_date', 'EMI start date', 'date'],
  ['disbursed_by', 'Disbursed by', 'text'],
  ['reference_no', 'Reference / UTR', 'text'],
  ['disbursed_on', 'Disbursed on', 'date'],
];

/** Upload the bank's disbursement order, read it, and show what moved against the offer. */
export const OfferDisbursement = ({ leadId, offer, onSaved }) => {
  const d = offer.disbursement || {};
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, d[k] ?? ''])));
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const variance = d.variance || [];
  const docs = (offer.documents || []).filter((x) => x.kind === 'DISBURSEMENT_ORDER');

  const sync = (data) => {
    const fresh = (data.offers || []).find((o) => o.id === offer.id)?.disbursement || {};
    setForm(Object.fromEntries(FIELDS.map(([k]) => [k, fresh[k] ?? ''])));
    onSaved(data);
  };

  const readDoc = async (doc) => {
    setReading(true);
    try {
      const res = await loanProfilingApi.extractDisbursement(leadId, offer.id, doc.id);
      sync(res.data);
      toast.success('Disbursement details read from the document');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read that document');
    } finally { setReading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      // every field goes, cleared ones as null — a wrong figure read off the
      // order has to be removable, not sticky
      const body = {};
      FIELDS.forEach(([k, , type]) => {
        const v = form[k];
        body[k] = (v === '' || v === null || v === undefined)
          ? null
          : (type === 'number' ? parseFloat(v) : v);
      });
      const res = await loanProfilingApi.saveDisbursement(leadId, offer.id, body);
      sync(res.data);
      toast.success('Disbursement saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`offer-disbursement-${offer.id}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-400">Upload the bank's order or disbursal message — the details are read for you.</p>
        <OfferUpload leadId={leadId} docType="disbursement_order" label="Upload order" busyLabel="Uploading…"
          testId={`disb-upload-${offer.id}`} onUploaded={(doc) => readDoc(doc)} />
      </div>

      {docs.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3" data-testid={`disb-docs-${offer.id}`}>
          {docs.map((x) => (
            <div key={x.doc_id} className="inline-flex items-center gap-2">
              <DocLink leadId={leadId} docId={x.doc_id} name={x.file_name} testId={`disb-doc-${offer.id}-${x.doc_id}`} />
              <button onClick={() => readDoc({ id: x.doc_id })} disabled={reading}
                className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
                data-testid={`disb-reread-${offer.id}-${x.doc_id}`}>
                {reading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}Read again
              </button>
            </div>
          ))}
        </div>
      )}

      {reading && (
        <p className="text-xs text-blue-600 inline-flex items-center gap-1.5 mb-3" data-testid={`disb-reading-${offer.id}`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />Reading the document…
        </p>
      )}

      {variance.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 mb-3" data-testid={`disb-variance-${offer.id}`}>
          <p className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1.5 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />The bank disbursed on different terms
          </p>
          <ul className="space-y-1">
            {variance.map((v) => (
              <li key={v.field} className="text-xs text-amber-900" data-testid={`disb-variance-${offer.id}-${v.field}`}>
                <strong>{v.label}:</strong> offered {v.unit === '₹' ? inr(v.offered) : `${v.offered}${v.unit === '%' ? '%' : ` ${v.unit}`}`}
                {' → '}disbursed {v.unit === '₹' ? inr(v.disbursed) : `${v.disbursed}${v.unit === '%' ? '%' : ` ${v.unit}`}`}
                {v.note && <span className="text-amber-700 italic"> — {v.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.net_disbursement_amount && variance.length === 0 && (
        <p className="text-xs text-emerald-700 inline-flex items-center gap-1.5 mb-3" data-testid={`disb-match-${offer.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />Disbursed exactly on the accepted terms.
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {FIELDS.map(([key, label, type]) => (
          <Field key={key} label={label}>
            <Input type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
              value={form[key] ?? ''} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
              className="mt-1 h-9" data-testid={`disb-${key}-${offer.id}`} />
          </Field>
        ))}
      </div>

      <div className="flex items-center justify-between mt-3">
        <p className="text-[11px] text-slate-400">
          {d.source === 'OCR' ? 'Read from the uploaded document — correct anything the bank printed differently.' : 'Typed in by the desk.'}
        </p>
        <Button size="sm" onClick={save} disabled={saving} data-testid={`disb-save-${offer.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Save disbursement
        </Button>
      </div>
    </div>
  );
};
